import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { SEASONS, SEASON_LINKS } from "@/lib/seasons";
import ProductCard from "@/components/ProductCard";
import { MaskedLine, Reveal } from "@/components/Reveal";

function SeasonSwitcher({ active }) {
  return (
    <div className="flex flex-wrap gap-2">
      {SEASON_LINKS.map((s) => (
        <Link
          key={s.slug}
          to={s.to}
          data-testid={`season-switch-${s.slug}`}
          className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.12em] border transition-colors duration-300 ${
            active === s.slug
              ? "bg-gold text-ink border-gold"
              : "border-cream/25 text-cream/70 hover:border-gold/60 hover:text-cream"
          }`}
        >
          {s.label}
        </Link>
      ))}
    </div>
  );
}

export default function Season() {
  const { slug } = useParams();
  const season = SEASONS[slug];
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!season) return;
    setProducts([]);
    api.get("/products")
      .then((r) => setProducts(r.data.filter((p) => season.picks.includes(p.category)).slice(0, 6)))
      .catch(() => {});
  }, [slug]);

  if (!season) {
    return (
      <main className="pt-40 pb-32 px-6 text-center min-h-screen">
        <p className="font-serif text-3xl text-ink">That season hasn't been unwrapped yet.</p>
        <Link to="/festive" className="mt-6 inline-block text-gold-brass gold-underline-btn text-sm font-medium">
          See the Festive Edit
        </Link>
      </main>
    );
  }

  const enquireTo = `/enquire?occasion=${encodeURIComponent(season.occasion)}&message=${encodeURIComponent(season.message)}`;

  return (
    <main>
      <section className="relative bg-ink-deep text-cream overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_10%,rgba(212,175,55,0.14)_0%,rgba(26,24,23,0)_60%)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-24 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
              <SeasonSwitcher active={slug} />
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="eyebrow !text-gold/80 flex items-center gap-3 mt-8"
            >
              <span className="inline-block h-px w-10 bg-gold" />
              {season.eyebrow}
            </motion.p>
            <h1 className="mt-7 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-balance">
              <MaskedLine delay={0.35}>{season.line1}</MaskedLine>
              <MaskedLine delay={0.5} className="italic text-gold">{season.line2}</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-md text-base text-cream/60 leading-relaxed"
            >
              {season.subcopy}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <Link
                to={enquireTo}
                data-testid="season-cta-quote"
                className="inline-flex items-center gap-2 bg-gold text-ink px-8 py-4 text-sm font-medium tracking-wide hover:bg-gold-hover transition-colors duration-400 group"
              >
                {season.cta}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href="#season-picks" className="text-sm text-cream/80 gold-underline-btn font-medium">
                See this season's picks
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto max-w-[340px] sm:max-w-[400px]"
            >
              <div className="absolute -inset-3 border border-gold/30 rounded-t-[999px]" />
              <div className="overflow-hidden rounded-t-[999px] shadow-[0_40px_80px_rgba(0,0,0,0.45)]">
                <img src={season.heroImage} alt={`${season.name} Gratiora hamper`} className="w-full aspect-[3/4] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="season-picks" className="py-20 lg:py-28 px-6 lg:px-12 max-w-7xl mx-auto">
        <Reveal>
          <p className="eyebrow">This season's picks</p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
            Six pieces made for {season.name}
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-cream-dark/60 border-y border-gold/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="eyebrow">The {season.name} clock</p>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight text-balance">
              {season.name} doesn't wait — here's the run-up
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {season.timeline.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.1}>
                <div data-testid={`season-timeline-${i}`} className="relative">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold-brass">{t.date}</span>
                  <div className="mt-3 h-px w-full bg-gradient-to-r from-gold/60 to-transparent" />
                  <h3 className="mt-5 font-serif text-xl sm:text-2xl text-ink">{t.title}</h3>
                  <p className="mt-3 text-sm text-ink-muted leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14 max-w-2xl">
            <ul className="space-y-3">
              {season.notes.map((n) => (
                <li key={n} className="flex items-start gap-3 text-sm text-ink/80">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rotate-45 bg-gold shrink-0" />
                  {n}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-[1.1] text-balance max-w-3xl mx-auto">
            Reserve your production slot <span className="italic text-gold-brass">before the season fills.</span>
          </h2>
          <Link
            to={enquireTo}
            data-testid="season-bottom-cta"
            className="mt-10 inline-flex items-center gap-2 bg-ink text-cream px-10 py-5 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 group"
          >
            {season.cta}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
